// The one screen on this console that hands over personal data.
//
// EVERYTHING BELOW IS ARRANGED AROUND ONE SENTENCE FROM THE BRIEF: the purpose
// must be an explicit, deliberate choice, never a default and never a checkbox
// somebody clicks past. Four mechanisms carry that, and each is here because
// leaving it out produces a screen that looks identical and behaves worse.
//
//   NOTHING IS SELECTED WHEN THE SCREEN OPENS. Not `participation_report`, which
//   is the service's default and the widest file it can produce. A preselected
//   radio is a choice somebody made by pressing a button next to it, and the one
//   it would have made for them is the one that carries every column of every
//   attendee.
//
//   OPENING THE SCREEN READS NOTHING. Every other surface here fetches on mount,
//   which is right for a report and wrong for this: the request is a disclosure
//   of attendee personal data and it writes a `report_access` row naming the
//   person who made it. Clicking a tab must not put somebody's name against a
//   file they never asked for.
//
//   THE REQUEST NAMES THE PURPOSE. exportUrl takes it as a required argument, so
//   there is no call this file can make that leaves the server to fall back to
//   its default. The console never relies on that default being the safe one.
//
//   THE THREE PURPOSES ARE THE SERVICE'S OWN, IMPORTED RATHER THAN RESTATED.
//   `purposeFor` gives each one's sentence, the consents it requires and the
//   columns it carries, out of the same module the server resolves the request
//   with. A console with its own copy of "a marketing list requires three
//   consents" is a console that keeps saying so after somebody adds a fourth.
//   The import is safe for a browser bundle for the same reason the endpoint
//   constants are: exportPurpose.ts imports nothing at runtime, and check.mjs
//   asserts that rather than trusting it.
//
// THE MANIFEST IS SHOWN AND NOT DISCARDED, which is the other half of the brief.
// It is the part of the file that says what the file is, who is missing from it
// and on which requirement, what Nova does with the data afterwards, and what
// the client is now responsible for. A console that downloaded the JSON and drew
// a tick would have delivered the rows and thrown away the only part written to
// be read by a person.
//
// AND NO ATTENDEE'S DETAILS ARE DRAWN ON THIS SCREEN. The rows are what the
// client asked for and they are not what a console is for: a name on a screen is
// a name on a shoulder-surfed laptop, in a screenshot pasted into a chat, and in
// a support session shared over a call. They are held in memory long enough to
// become a file and they reach no element, no attribute and no url. check.mjs
// looks for the fixture's own names and addresses in the rendered markup rather
// than in its text, because the last time this repository lost a secret to a
// screen it was in an aria-label.
//
// ---------------------------------------------------------------------------
// TWO STEPS, TWO PAGES, AS THE DESIGN DRAWS THEM (TASKS §143, step 6)
//
// handoff/console-data.jsx is two components — DataChoose and DataProduced —
// and a wizard that walks between them. This was one page, the file appended
// under the chooser, on the argument that a second file under a second
// purpose should be no further away than the control. Gabe's ruling (9 Oct)
// is that the design wins on layout, so a file now has its page: the wizard at
// step two, with "1. What is this file for?" as the way back, which keeps the
// second file one press away. The chooser offers "2. The file" back again for
// as long as this tab holds one, so going back to look costs nothing either.
//
// ---------------------------------------------------------------------------
// AND THE LIST OF FILES ALREADY PRODUCED, WHICH THIS FILE USED TO SAY IT COULD
// NOT DRAW
//
// What stood here was that the record was real — every export writes a
// `report_access` row naming the activation, the purpose and whoever asked —
// and that no endpoint answered "what has this activation handed over", so the
// section said where the record lived instead of drawing a list out of nothing.
// It also said what the wrong fix would have been, and that paragraph is worth
// keeping because it is still true: a history assembled in a browser out of what
// this tab happens to have produced would be the most misleading thing on the
// screen, because it would look like the client's entitlement and would be one
// tab's memory.
//
// There is a read now. `GET .../export-history` returns those rows, and the
// table below is drawn from them and from nothing else.
//
// IT IS DRAWN FOR EVERY ROLE THE PAGE OPENS FOR. This section used to sit
// inside `role === "nova_admin"`, and it came out when the read landed, so a
// client viewer refused the export was still shown its history. Since the
// 11 Oct rulings the export is a per-person switch (149), and this page opens
// only for somebody the service lets take a file: an Admin, or an Editor or a
// Viewer with their switch on (roleAccess.ts, `data`). The service still
// answers the history to anybody granted the show; whether a person with the
// switch off should be shown it somewhere is an open question, not this page's.
//
// THE DESIGN'S FIFTH COLUMN IS NOT DRAWN. It is "Where it went", with values
// like "downloaded once, then the link expired". Nothing in this system knows
// that: there is no download record, there are no expiring links, and the audit
// row's `destination` is the constant `download` on every row ever written. The
// column is omitted rather than filled with that constant, and the note under
// the table says plainly that nothing follows a file after it is produced —
// which is the honest version of what the design was reaching for and is worth
// a client knowing.

import { DataTable } from "@nova/desk-ui/metrics";
import { NoticeBox, PageHead, Wizard } from "@nova/desk-ui/parts";
import { Empty, SecLabel } from "@nova/desk-ui/kit";
import { ListState } from "@nova/desk-ui/states";
import { useEffect, useState, type ReactNode } from "react";
import type { ActivationExport } from "../../../operator/src/export.js";
import type {
  ExportHistory,
  ExportHistoryLine,
  ExportHistoryState,
} from "../../../operator/src/exportHistory.js";
import {
  EXPORT_PURPOSE_IDS,
  purposeFor,
  type ExportPurposeSpec,
} from "../../../operator/src/exportPurpose.js";
import { readExport, readExportHistory, type ApiOptions, type Failure } from "../api/client.js";
import { count, moment } from "../operator/present.js";
import { ReadFailed } from "./ReadFailed.js";
import { Refusal } from "./Refusal.js";
import { useShow } from "./Show.js";
import type { SessionToken } from "../session/store.js";

export type ExportProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

type State =
  /** Nothing asked for, nothing read. Where this screen opens and stays until somebody chooses. */
  | { readonly phase: "choosing" }
  | { readonly phase: "producing"; readonly purpose: string }
  | { readonly phase: "produced"; readonly purpose: string; readonly artefact: ActivationExport }
  | { readonly phase: "failed"; readonly purpose: string; readonly failure: Failure };

export function Export({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
}: ExportProps): ReactNode {
  const show = useShow();
  const [state, setState] = useState<State>({ phase: "choosing" });
  /**
   * Which purpose is selected, before it is asked for.
   *
   * Null is the initial value and is the whole mechanism. It is not "the default
   * purpose, not yet overridden": there is no purpose until somebody names one,
   * the button that produces a file is disabled while it is null, and no request
   * can be built without it.
   */
  const [chosen, setChosen] = useState<string | null>(null);
  const [file, setFile] = useState<{ readonly url: string; readonly name: string } | null>(null);
  // Which of the two steps is on the page. The file's only once there is one.
  const [viewing, setViewing] = useState<"choose" | "file">("choose");
  // Bumped by the history's "Ask again"; the history read runs again when it changes.
  const [historyAttempt, setHistoryAttempt] = useState(0);

  /**
   * The history, which is the one thing on this screen that is read on mount.
   *
   * WHICH LOOKS LIKE A CONTRADICTION OF THIS FILE'S SECOND RULE and is not, so
   * it is worth being explicit. "Opening the screen reads nothing" is about the
   * export: that request discloses attendee personal data and files a
   * `report_access` row against the person who made it, so a tab must not put
   * somebody's name against a file they never asked for. This request discloses
   * no personal data, names nobody, and files no audit row at all — the service
   * argues that decision in reports/audit.ts. Fetching it on mount is what makes
   * the section a record rather than a button somebody has to know to press.
   *
   * The `reason` on a failure is kept rather than mapped to a boolean, because a
   * 403 here means something specific: a viewer whose grant was revoked between
   * signing in and opening this tab.
   */
  const [history, setHistory] = useState<
    | { readonly phase: "reading" }
    | { readonly phase: "ready"; readonly value: ExportHistory }
    | { readonly phase: "failed"; readonly failure: Failure }
  >({ phase: "reading" });

  /** Which purpose the history is filtered to, or every one of them. */
  const [shown, setShown] = useState<string>(EVERY_PURPOSE);

  useEffect(
    // Revoked when it is replaced and when this screen closes. An object URL is
    // a live handle to a blob of attendee personal data held by the browser, and
    // one that is never revoked keeps that blob alive for as long as the
    // document does — including after somebody has navigated away from the
    // screen that made it.
    () => () => {
      if (file !== null) URL.revokeObjectURL(file.url);
    },
    [file],
  );

  /**
   * Read the history, and read it again once a file has been produced.
   *
   * `produced` is in the dependency list rather than the history being appended
   * to in `produce`, and the difference matters on the one screen where it
   * would. A row pushed on locally would be this tab's belief about what the
   * server recorded; asking again is the server's own answer, which is the only
   * thing this section claims to be. If the audit write failed, this table must
   * not show a file the record does not have.
   */
  const produced = state.phase === "produced" ? state.artefact.manifest?.generated_at : null;

  useEffect(() => {
    // AFTER THE DIRECTORY AND NOT IN THE SAME TICK AS IT, which is Ride.tsx's
    // rule and is here for a reason that showed up as a failing check rather
    // than as a judgement: this effect ran first, the frame's own read went out
    // second, and a recorder that answers in order handed the history the
    // directory's body and the directory the history's. Against the real service
    // nothing would break, and the show's name would simply be slower to appear
    // than it needs to be — but the frame's read is the one that decides whether
    // this screen is allowed to be here at all, and it should not be queued
    // behind a section of it.
    if (show.status !== "ready") return undefined;

    let cancelled = false;
    setHistory({ phase: "reading" });

    void (async () => {
      const result = await readExportHistory<ExportHistory>(api, token, activationId);
      if (cancelled) return;
      if (!result.ok) {
        setHistory({ phase: "failed", failure: result });
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        return;
      }
      setHistory({ phase: "ready", value: result.value });
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, produced, show.status, historyAttempt]);

  async function produce(purpose: string): Promise<void> {
    setState({ phase: "producing", purpose });

    const result = await readExport<ActivationExport>(api, token, activationId, purpose);

    if (!result.ok) {
      setState({ phase: "failed", purpose, failure: result });
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      return;
    }

    setState({ phase: "produced", purpose, artefact: result.value });
    setFile(fileFor(activationId, purpose, result.value));
    setViewing("file");
  }

  if (state.phase === "failed") {
    return (
      <Refusal
        onRetry={() => void produce(state.purpose)}
        what="Could not create the export"
        failure={state.failure}
        role={role}
        onEnrol={onEnrol}
        notCounted="No file was created or downloaded."
      />
    );
  }

  if (state.phase === "produced" && viewing === "file") {
    return (
      <div className="stack">
        <PageHead
          title="Data"
          lede="Create a consent-checked export for a specific purpose."
          uuid={activationId}
          explain="info"
        />
        <Wizard steps={STEPS} at={1} onGo={(index) => (index === 0 ? setViewing("choose") : undefined)} />
        <Produced artefact={state.artefact} file={file} />
      </div>
    );
  }

  const held = state.phase === "produced";

  return (
    <div className="stack">
      <PageHead
        title="Data"
        lede="Create a consent-checked export for a specific purpose."
        uuid={activationId}
        explain="info"
      />

      <form
        className="panel panel-stack"
        onSubmit={(event) => {
          event.preventDefault();
          // Refused rather than defaulted, which is the same decision
          // purposeFor makes on the server and for the same reason: the file a
          // fallback would produce is the widest one this system can make.
          if (chosen === null) return;
          void produce(chosen);
        }}
      >
        {/* Inside the panel with the choice it is the first step of, as the
            design draws it. The note is what an empty choice needs said about
            it and is dropped once something is selected, because by then the
            reader has answered the thing it was explaining. Its steps become
            tabs only while this tab holds a file, so the file can be gone
            back to; the design's chooser has none, and has no file to go to. */}
        <Wizard
          steps={STEPS}
          at={0}
          {...(held ? { onGo: (index: number) => (index === 1 ? setViewing("file") : undefined) } : { tabs: false })}
          {...(chosen === null ? { note: NOTHING_CHOSEN } : {})}
        />
        <fieldset className="set">
          <legend className="label">Purpose</legend>
          <div className="rows data-purposes">
            {EXPORT_PURPOSE_IDS.map((id) => (
              <Purpose
                key={id}
                spec={purposeFor(id)}
                chosen={chosen === id}
                disabled={state.phase === "producing"}
                onChoose={() => setChosen(id)}
              />
            ))}
          </div>
        </fieldset>

        <div className="acts acts-flush">
          <button
            type="submit"
            className="btn"
            disabled={chosen === null || state.phase === "producing"}
          >
            {state.phase === "producing" ? "Creating…" : "Create export"}
          </button>
        </div>
      </form>

      {/* FOR WHOEVER THIS PAGE OPENS FOR, AS THE DESIGN DRAWS IT (12 Oct).
          These two notes sat inside `role === "nova_admin"` while a client
          viewer was shown this page without the export, and an Editor lost
          them with the viewer. The page now opens only for somebody the
          service lets take a file (roleAccess.ts, `data`), and both notes are
          about the file they take. */}
      <section>
        <SecLabel>People</SecLabel>
        <div className="panel">
          <p>Names stay off this screen. They are included only in the export you create.</p>
          <p className="help help-tight">
            Somebody an Admin withheld is left out of every file produced from here on, and somebody whose
            details were erased is a row with nobody&apos;s details on it, so the count stays true. A file
            already produced does not change.
          </p>
        </div>
      </section>

      <History
        history={history}
        shown={shown}
        onShow={setShown}
        role={role}
        onEnrol={onEnrol}
        onRetry={() => setHistoryAttempt((n) => n + 1)}
      />

      {/* After the history, as the design orders the page: what is made, what
          has been made, and then what form a file takes. */}
      <section>
        <SecLabel>File security</SecLabel>
        <div className="panel">
          <p>Password-protected files are not supported yet. Exports are JSON.</p>
        </div>
      </section>
    </div>
  );
}

/** Every purpose, as the filter's own option rather than as an absent value. */
const EVERY_PURPOSE = "Every purpose";

/**
 * What has already been handed over.
 *
 * THE FILTER BAR STAYS DRAWN IN EVERY STATE, which is ListState's own rule and
 * the reason it is used here rather than three branches: a select that vanishes
 * while the page is reading is a select that loses what somebody just chose.
 *
 * A REFUSAL IS DRAWN AS A REFUSAL AND NOT AS AN EMPTY TABLE, which on this
 * section is the failure mode worth naming. 129's policy answers a viewer with
 * no rows rather than an error, and the endpoint turns that into a 403 for
 * exactly this reason — so if this ever renders an empty history for a client
 * whose grant has gone, the refusal has stopped arriving and the screen is
 * telling somebody their attendees' data has never left the building.
 */
function History({
  history,
  shown,
  onShow,
  role,
  onEnrol,
  onRetry,
}: {
  history:
    | { readonly phase: "reading" }
    | { readonly phase: "ready"; readonly value: ExportHistory }
    | { readonly phase: "failed"; readonly failure: Failure };
  shown: string;
  onShow: (purpose: string) => void;
  role: string;
  onEnrol: () => void;
  onRetry: () => void;
}): ReactNode {
  if (history.phase === "failed") {
    // A section of the page, so the failure sits under its own label rather
    // than taking the page's heading.
    return (
      <section>
        <SecLabel>Export history</SecLabel>
        <div className="panel panel-stack">
          <ReadFailed
            what="The history did not come back"
            failure={history.failure}
            role={role}
            onEnrol={onEnrol}
            notCounted="The history did not load. This does not mean no exports exist."
            onRetry={onRetry}
          />
        </div>
      </section>
    );
  }

  const value = history.phase === "ready" ? history.value : null;
  const lines = value?.files ?? [];
  const matching = shown === EVERY_PURPOSE ? lines : lines.filter((line) => line.purpose === shown);

  // HOW MANY ROWS THE FILTER IS HIDING BECAUSE THEY HAVE NO PURPOSE TO MATCH,
  // which is the one way this screen could still drop an incomplete row after
  // all the care taken not to. A refused, failed or unaccounted-for row carries
  // no purpose — 011 refuses a `detail` before the completion — so every one of
  // them fails `line.purpose === shown` and disappears the moment somebody
  // narrows the table. Counted and said rather than left to be noticed.
  const hiddenWithoutPurpose =
    shown === EVERY_PURPOSE ? 0 : lines.filter((line) => line.purpose === null).length;

  return (
    <section>
      <SecLabel info="Nova's audit record identifies the account that created each export.">
        Export history
      </SecLabel>
      <div className="panel panel-stack">
        <div className="filters">
          <label className="field">
            <span className="label">Purpose</span>
            <select className="input" value={shown} onChange={(event) => onShow(event.target.value)}>
              {[EVERY_PURPOSE, ...EXPORT_PURPOSE_IDS].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* NO `onRetry`, WHICH IS DELIBERATE AND MATCHES EVERY OTHER SURFACE
            HERE. ListState only draws its own `Failed` panel, with the "Ask
            again" button on it, when its state is "failed" — and this never
            passes that, because a failed history read is drawn as a Refusal
            above instead. Refusal tells a 403 from a 500 and offers an admin the
            enrolment screen the service's own sentence points at, which a
            generic retry cannot. Passing a handler here would have been a prop
            that could never fire. */}
        <ListState
          state={history.phase === "reading" ? "reading" : "ready"}
          rows={matching}
          cols={5}
          unit="files"
          empty={
            lines.length === 0 ? (
              <Empty title="No exports yet">
                <p>{value?.empty_note ?? ""}</p>
              </Empty>
            ) : (
              <Empty title="No exports match this purpose">
                <p>
                  {lines.length === 1
                    ? "One request is on this record"
                    : `${lines.length} requests are on this record`}
                  , under other purposes or with no purpose recorded.
                </p>
              </Empty>
            )
          }
        >
          {/* THE DESIGN'S FIFTH COLUMN WAS "WHERE IT WENT" AND THIS ONE IS THE
              STATE. Nothing follows a file after it is produced, so that column
              could only ever have been one repeated word; what the record does
              know, and what the design had no column for, is that a row is not
              always a file. See the header. */}
          <DataTable
            cols={["When", "Purpose", "Rows", "Produced by", "State"]}
            rows={matching.map((line) => [
              moment(line.started_at),
              line.purpose ?? <span className="quiet">not recorded</span>,
              line.state === "produced" ? (
                count(line.row_count)
              ) : (
                <span className="quiet">no file</span>
              ),
              line.produced_by,
              <span className={tagFor(line.state)}>{line.state}</span>,
            ])}
          />
        </ListState>

        {/* One sentence per state that is actually on this record, rather than
            the same sentence repeated down a column. The word in the tag is a
            status badge on its own, and three of these four words mean
            something a reader would otherwise guess at. */}
        {statesIn(matching).length === 0 ? null : (
          <ul className="reasons">
            {statesIn(matching).map((state) => (
              <li key={state}>
                <strong>{state}.</strong>{" "}
                {matching.find((line) => line.state === state)?.state_note ?? ""}
              </li>
            ))}
          </ul>
        )}

        {hiddenWithoutPurpose === 0 ? null : (
          <p className="help help-tight">
            {hiddenWithoutPurpose === 1
              ? "One request without a recorded purpose is hidden by this filter."
              : `${hiddenWithoutPurpose} requests without a recorded purpose are hidden by this filter.`}
            {" "}Choose every purpose to see them.
          </p>
        )}

        {value?.truncated !== true ? null : (
          <p className="help help-tight">
            The most recent {lines.length} are listed and this activation has more. The counts above
            each cover the whole record rather than the part that is drawn.
          </p>
        )}

        <p className="help help-tight">{value?.actor_note ?? ""}</p>
        <p className="help help-tight">{value?.record_note ?? ""}</p>
        <p className="help help-tight">
          Console cannot track or recall a file after it is downloaded.
        </p>
      </div>
    </section>
  );
}

/**
 * Which states are on the record, in the order the operator declares them.
 *
 * Ordered by that constant rather than by first appearance, so the legend does
 * not reshuffle itself when the filter changes.
 */
function statesIn(lines: readonly ExportHistoryLine[]): ExportHistoryState[] {
  const present = new Set(lines.map((line) => line.state));
  return STATE_ORDER.filter((state) => present.has(state));
}

const STATE_ORDER: readonly ExportHistoryState[] = ["produced", "refused", "failed", "incomplete"];

/**
 * The tag for one state.
 *
 * FOUR STATES ONTO THREE EXISTING TAG CLASSES, and no new colour anywhere. The
 * mapping is the reading rather than a palette:
 *
 *   `produced` is the plain tag. A file existing is the fact this table is a
 *   list of, and it is neither good news nor bad.
 *
 *   `refused` is the good one, which looks wrong for a second and is not.
 *   Nothing was handed over, because this service declined to: the rule worked.
 *
 *   `failed` and `incomplete` both warn, and they are not collapsed into one
 *   word anywhere else in this feature — the state, the sentence beside it and
 *   the tally all keep them apart. What they share is that somebody at Nova
 *   should look, which is what the amber is for.
 */
function tagFor(state: ExportHistoryState): string {
  if (state === "produced") return "tag";
  if (state === "refused") return "tag tag-good";
  return "tag tag-warn";
}

/** The two questions this screen asks, in the design's own words. */
const STEPS: readonly string[] = ["What is this file for?", "The file"];

/**
 * Why the screen opens with nothing selected.
 *
 * Said where somebody who has not chosen yet is looking, rather than beside the
 * button, because the sentence is about the three rows below it: they are three
 * files and not three ways of asking for one, and the difference between them is
 * who is in the file rather than how much of each person it carries.
 */
const NOTHING_CHOSEN =
  "Choose what the export is for. The purpose decides who is included and which columns it carries.";

/**
 * One purpose, described in the service's own words.
 *
 * `says` and `responsibility` are the sentences exportPurpose.ts writes into the
 * manifest of the file itself, shown here *before* the choice rather than only
 * after it. A description a person reads once the file exists is a description
 * they read after the disclosure, which is the wrong end of it.
 *
 * The required consents are named because they are what turns a purpose into a
 * different set of people rather than a different set of columns. A marketing
 * list is not a participation report with fewer fields; it is the people who
 * agreed to all three, and everybody else is absent from it.
 *
 * THE DESIGN'S ROW, WITH THE DESIGN'S SELECTED MARK MADE HEAVIER. `.row-on`
 * tints a chosen row and gives it a hairline; reports.css thickens that and
 * puts weight on the title. This is the control where "which one is selected"
 * decides whether a file carries names, and the reader it has to survive is
 * somebody glancing at a laptop in bright sun on a train.
 */
function Purpose({
  spec,
  chosen,
  disabled,
  onChoose,
}: {
  spec: ExportPurposeSpec;
  chosen: boolean;
  disabled: boolean;
  onChoose: () => void;
}): ReactNode {
  return (
    <label className={chosen ? "row-pick row-on data-purpose" : "row-pick data-purpose"}>
      <input
        type="radio"
        name="export-purpose"
        value={spec.id}
        checked={chosen}
        disabled={disabled}
        onChange={onChoose}
      />
      <span>
        <span className="t">{TITLES[spec.id] ?? spec.id}</span>
        <span className="d data-key">{spec.id}</span>
        <span className="d">{spec.says}</span>
        <span className="d">
          {spec.requires.length === 0
            ? "Includes everyone who took part. Consent still controls which values appear in each row."
            : `Includes only people with current consent for ${spec.requires.join(", ")}. Everyone else is left out.`}
        </span>
        <span className="d">{spec.responsibility}</span>
      </span>
    </label>
  );
}

/**
 * The manifest, and the file itself.
 *
 * ORDERED SO THAT WHAT THE FILE IS COMES BEFORE HOW TO GET IT. The download is
 * at the bottom on purpose: a button above the retention position is a button
 * people press instead of reading it.
 */
function Produced({
  artefact,
  file,
}: {
  artefact: ActivationExport;
  file: { readonly url: string; readonly name: string } | null;
}): ReactNode {
  const manifest = artefact.manifest;
  const purpose = manifest?.purpose;
  const retention = manifest?.retention;

  return (
    <>
      <NoticeBox kind="amber" title="Export created">
        <p>
          {count(manifest?.row_count)} row(s) and {count(manifest?.withdrawal_count)} withdrawal record(s),
          as at {moment(manifest?.generated_at)}. The people are not shown on this screen.
        </p>
      </NoticeBox>

      <section>
        <SecLabel>Export details</SecLabel>
        <div className="panel panel-stack">
          <p>{purpose?.says ?? ""}</p>
          <p>
            <strong>Your responsibility.</strong> {purpose?.responsibility ?? ""}
          </p>
          <p className="id">
            Template {purpose?.template_id ?? "not reported"} · columns{" "}
            {(purpose?.columns ?? []).join(", ")}
            {purpose?.minimised === true
              ? ` · narrowed from ${(purpose.columns_available ?? []).join(", ")}`
              : ""}
          </p>
        </div>
      </section>

      {/* WHO IS NOT IN IT, WHICH IS THE NUMBER A MARKETING LIST IS READ WITHOUT.
          exportPurpose.ts drops people who declined rather than blanking them,
          precisely so the file needs no filtering before use — and the cost of
          that is a file that does not add up against the participation report
          unless somebody says how many are missing and why. This is that
          sentence, on the screen, beside the file it is about. */}
      {purpose === undefined || purpose.omitted === undefined ? null : (
        <section>
          <SecLabel>People not included</SecLabel>
          <div className="panel panel-stack">
            <p>
              <strong>{count(purpose.omitted.total)}</strong> counted registration(s) are absent from this
              file.
            </p>
            {(purpose.omitted.by_requirement ?? []).length === 0 ? null : (
              <ul className="reasons">
                {purpose.omitted.by_requirement.map((requirement) => (
                  <li key={requirement.purpose}>
                    {requirement.purpose}: {count(requirement.withdrawn)} agreed and took it back,{" "}
                    {count(requirement.never)} have no live consent and no withdrawal.
                  </li>
                ))}
              </ul>
            )}
            <p className="help help-tight">{purpose.omitted.note ?? ""}</p>
          </div>
        </section>
      )}

      {/* The ambiguity optIn.ts found, where it bites hardest. A required
          consent with no event of any kind at this activation means either
          nobody was asked or everybody refused, and one of those means this file
          is empty for a reason that has nothing to do with consent. */}
      {(purpose?.unobserved_requirements?.purposes ?? []).length === 0 ? null : (
        <NoticeBox
          kind="amber"
          title={`No consent activity recorded for ${purpose?.unobserved_requirements?.purposes.join(", ") ?? ""}`}
        >
          <p>{purpose?.unobserved_requirements?.note ?? ""}</p>
        </NoticeBox>
      )}

      {/* THE TWO HALVES, KEPT APART. Conflating them is how a client ends up
          believing Nova deletes something it does not: one answer is about the
          system this file came from, the other is about the file, and they have
          different owners. A null period is a value here rather than an absence
          — it means no period exists, which is a sentence somebody can argue
          with, and "unknown" would not be. */}
      {retention === undefined ? null : (
        <section>
          <SecLabel>Retention</SecLabel>
          <div className="panel panel-stack">
            <div>
              <p>
                <strong>On Nova's side.</strong> {retention.server?.says ?? ""}
              </p>
              <p className="id">
                Deletes attendee data: {retention.server?.deletes_attendee_data === true ? "yes" : "no"} ·{" "}
                {retention.server?.erases_on_request === true ? "erases a person on request: yes · " : ""}
                retention period: {months(retention.server?.period_months)}
              </p>
            </div>
            <div>
              <p>
                <strong>This file, once you have it.</strong> {retention.this_file?.says ?? ""}
              </p>
              <p className="id">
                Held by {retention.this_file?.held_by ?? "not reported"} · retention period:{" "}
                {months(retention.this_file?.period_months)}
              </p>
            </div>
            <p className="id">{retention.source ?? ""}</p>
          </div>
        </section>
      )}

      <section>
        {/* Where the file was read up to, behind the heading's (i), as the
            October hand-off has it: the position is this file's own, from the
            manifest, not the hand-off's sample figure. The voided note went
            with the hand-off; the access line stays, because it says under
            whose sight the counts above were taken. */}
        <SecLabel
          info={`Exported at record position ${count(manifest?.spine_high_water)}. A later export may include data that arrived from an offline tablet.`}
        >
          Withheld and excluded data
        </SecLabel>
        <div className="panel panel-stack">
          <p>
            {count(manifest?.withheld?.withdrawn)} row(s) carry less than they could because somebody took
            a consent back, and {count(manifest?.withheld?.never_granted)} because it was never given. Those
            people are in the file; a column of theirs is empty and the row says which.
          </p>
          {manifest?.admin_actions === undefined ? null : (
            <p>
              {count(manifest.admin_actions.withheld_and_absent)} person(s) an Admin withheld are not in this
              file. {count(manifest.admin_actions.erased)} person(s) whose details were erased
              {manifest.admin_actions.erased_and_absent > 0
                ? " are not in it either, because it requires a consent"
                : " are in it as rows with nobody's details on them"}
              .
            </p>
          )}
          <p className="help help-tight">
            {manifest?.access_note ?? "Access details are unavailable."}
          </p>
        </div>
      </section>

      {(manifest?.caveats ?? []).length === 0 ? null : (
        <section>
          <SecLabel>Before you use it</SecLabel>
          <div className="panel">
            <ul className="reasons">
              {(manifest?.caveats ?? []).map((caveat, index) => (
                <li key={index}>{caveat}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section>
        <SecLabel info="JSON containing the rows, withdrawals, consent wording and export details.">Download</SecLabel>
        <div className="panel panel-stack">
          {file === null ? (
            <p>
              The export was created and recorded, but this browser could not prepare the download.
            </p>
          ) : (
            <>
              <div className="acts acts-flush">
                {/* A download rather than a table. The href is a blob handle
                    scoped to this page and the name carries the activation and
                    the purpose so that two files in a downloads folder can be
                    told apart — and nobody's name, because a filename is the
                    part of a file that shows up in a screenshot. */}
                <a className="btn" href={file.url} download={file.name}>
                  Download {file.name}
                </a>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

/**
 * The file this artefact becomes, or null where the browser has no way to make
 * one.
 *
 * The whole response rather than the rows alone. The manifest and the consent
 * wording are what make the rows readable a year later — which consents these
 * people were shown, in what words, and what the file was for — and a download
 * of `rows` alone is the flattened artefact the purpose mechanism exists to
 * prevent, produced at the last step by the console.
 */
function fileFor(
  activationId: string,
  purpose: string,
  artefact: ActivationExport,
): { url: string; name: string } | null {
  if (typeof URL.createObjectURL !== "function") return null;

  const blob = new Blob([JSON.stringify(artefact, null, 2)], { type: "application/json" });
  return {
    url: URL.createObjectURL(blob),
    // Slugged rather than interpolated: an activation id arrives from the path
    // of the page, and a filename is somewhere a slash means something.
    name: `xp1-${slug(purpose)}-${slug(activationId)}.json`,
  };
}

const slug = (value: string): string => value.replace(/[^a-zA-Z0-9_-]+/g, "-").slice(0, 64);

/** A retention period in months, or the words for one that does not exist. */
function months(value: number | null | undefined): string {
  if (typeof value === "number" && Number.isFinite(value)) return `${value} months`;
  // Not "unknown". RETENTION.md's own first paragraph is that nothing in XP1
  // deletes attendee data, so the honest answer is that no period exists rather
  // than that nobody has told you what it is.
  return "none set";
}

/** What each purpose is called on a screen, where its identifier is a developer's word. */
const TITLES: Record<string, string> = {
  participation_report: "A record of who took part",
  attendance_only: "How many came, with nobody named",
  marketing_list: "People we may contact for marketing",
  survey_free_text: "Written survey answers",
  wheel_results: "What the prize wheel gave away",
};